/**
 * E-Signature Hook
 * Handles all e-signature API interactions including creation, verification, and retrieval
 */
import { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useESignature = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Create a new e-signature
   * @param {Object} signatureData - The signature data to save
   * @param {string} signatureData.signatureImage - Base64 encoded signature image
   * @param {string} signatureData.signerName - Name of the person signing
   * @param {string} signatureData.signerTitle - Title/role of the signer
   * @param {string} signatureData.documentType - Type of document being signed
   * @param {string} signatureData.documentId - ID of the document being signed
   * @param {string} signatureData.patientId - ID of the patient (if applicable)
   */
  const createSignature = useCallback(async (signatureData) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/esignature/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          signatureImage: signatureData.signatureImage || '',
          signerName: signatureData.signerName,
          signerTitle: signatureData.signerTitle || '',
          signerCredentials: signatureData.signerCredentials || '',
          documentType: signatureData.documentType,
          documentId: signatureData.documentId,
          patientId: signatureData.patientId || ''
        })
      });

      const data = await response.json();
      
      if (data.success) {
        return { success: true, signature: data.signature };
      } else {
        throw new Error(data.detail || 'Failed to create signature');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify an existing signature
   * @param {string} signatureId - The ID of the signature to verify
   */
  const verifySignature = useCallback(async (signatureId) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/esignature/verify/${signatureId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        return { success: true, verification: data.verification };
      } else {
        throw new Error(data.detail || 'Failed to verify signature');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get all signatures for a document
   * @param {string} documentId - The ID of the document
   */
  const getDocumentSignatures = useCallback(async (documentId) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/esignature/document/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        return { success: true, signatures: data.signatures };
      } else {
        throw new Error(data.detail || 'Failed to get signatures');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get signature certificate
   * @param {string} signatureId - The ID of the signature
   */
  const getSignatureCertificate = useCallback(async (signatureId) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/esignature/certificate/${signatureId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        return { success: true, certificate: data.certificate };
      } else {
        throw new Error(data.detail || 'Failed to get certificate');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get signature audit trail with filtering options
   * @param {Object} filters - Filter options
   * @param {string} filters.documentType - Filter by document type
   * @param {string} filters.patientId - Filter by patient ID
   * @param {string} filters.signerName - Filter by signer name (partial match)
   * @param {string} filters.startDate - Filter by start date (ISO string)
   * @param {string} filters.endDate - Filter by end date (ISO string)
   * @param {number} filters.limit - Number of results to return
   * @param {number} filters.skip - Number of results to skip (pagination)
   */
  const getAuditTrail = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.documentType) params.append('document_type', filters.documentType);
      if (filters.patientId) params.append('patient_id', filters.patientId);
      if (filters.signerName) params.append('signer_name', filters.signerName);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.skip) params.append('skip', filters.skip);
      
      const queryString = params.toString();
      const url = `${API_URL}/api/esignature/audit-trail${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        return { success: true, auditTrail: data.auditTrail, pagination: data.pagination };
      } else {
        throw new Error(data.detail || 'Failed to get audit trail');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get signature audit statistics
   */
  const getAuditStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/esignature/audit-trail/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        return { success: true, stats: data.stats };
      } else {
        throw new Error(data.detail || 'Failed to get audit stats');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    createSignature,
    verifySignature,
    getDocumentSignatures,
    getSignatureCertificate,
    getAuditTrail,
    getAuditStats
  };
};

export default useESignature;
